import api from '@/libs/api'
import store from '@/store'

const getProjectId = () => store.state.app.projectId

export default {
  namespaced: true,
  state: {
    serviceUnavailable: false,
    serviceRequested: false,
    firewalls: [],
    resultInfo: {},
    currentPage: 1,
    fetchingFirewalls: false,
    firewall: {},
    gettingFirewall: false,
    addingFirewall: false,
    updatingFirewall: false,
    removingFirewall: false,
    addingRule: false,
    updatingRule: false,
    removingRule: false,
    rulesUpdatedCount: 0,
  },
  getters: {
    currentPage: state => state.currentPage,
  },
  mutations: {
    SET_SERVICE_UNAVAILABLE(state, value) {
      state.serviceUnavailable = true
      state.serviceRequested = value
    },
    SET_FIREWALLS(state, value) {
      state.firewalls = value.result
      state.resultInfo = value.resultInfo
    },
    SET_FETCHING_FIREWALLS(state, value) {
      state.fetchingFirewalls = value
    },
    SET_FIREWALL(state, value) {
      state.firewall = value.result
    },
    SET_GETTING_FIREWALL(state, value) {
      state.gettingFirewall = value
    },
    SET_ADDING_FIREWALL(state, value) {
      state.addingFirewall = value
    },
    SET_UPDATING_FIREWALL(state, value) {
      state.updatingFirewall = value
    },
    UPDATE_FIREWALL(state, result) {
      state.firewall.name = result.name
    },
    SET_REMOVING_FIREWALL(state, value) {
      state.removingFirewall = value
    },
    REMOVE_FIREWALL(state, id) {
      state.firewalls = state.firewalls.filter(el => el.id !== id)
    },
    SET_ADDING_RULE(state, value) {
      state.addingRule = value
    },
    ADD_FIREWALL(state, res) {
      state.firewalls.push({
        id: res.id,
        name: res.name,
      })
    },
    SET_UPDATING_RULE(state, value) {
      state.updatingRule = value
    },
    SET_REMOVING_RULE(state, value) {
      state.removingRule = value
    },
    ADD_RULE(state, value) {
      state.firewall.boundRules.push(value)
    },
    UPDATE_RULE(state, payload) {
      const index = state.firewall.boundRules.findIndex(el => el.id === payload.id)
      state.firewall.boundRules[index] = payload.rule
      state.rulesUpdatedCount++
    },
    REMOVE_RULE(state, id) {
      state.firewall.boundRules = state.firewall.boundRules.filter(el => el.id !== id)
    },
    SET_CURRENT_PAGE(state, value) {
      state.currentPage = value
    },
  },
  actions: {
    fetchFirewalls({ commit }, payload) {
      const ext = payload ? `?PageIndex=${payload.pageIndex}&PageSize=${payload.pageSize}` : ''
      commit('SET_FETCHING_FIREWALLS', true)
      return new Promise((resolve, reject) => {
        api('compute')
          .get(`/projects/${getProjectId()}/firewall${ext}`)
          .then(res => {
            commit('SET_FIREWALLS', res.data)
            resolve(res)
          })
          .catch(err => {
            if (err.response?.status === 426 || err.response?.status === 428) {
              commit('SET_SERVICE_UNAVAILABLE', err.response?.status === 428)
            }
            reject(err)
          })
          .finally(() => {
            commit('SET_FETCHING_FIREWALLS', false)
          })
      })
    },
    getFirewall({ commit }, id) {
      commit('SET_GETTING_FIREWALL', true)
      return new Promise((resolve, reject) => {
        api('compute')
          .get(`/projects/${getProjectId()}/firewall/${id}`)
          .then(res => {
            commit('SET_FIREWALL', res.data)
            resolve(res)
          })
          .catch(err => {
            reject(err)
          })
          .finally(() => {
            commit('SET_GETTING_FIREWALL', false)
          })
      })
    },
    addFirewall({ commit }, firewall) {
      commit('SET_ADDING_FIREWALL', true)
      return new Promise((resolve, reject) => {
        api('compute')
          .post(`/projects/${getProjectId()}/firewall`, firewall)
          .then(res => {
            commit('ADD_FIREWALL', res.data.result)
            resolve(res)
          })
          .catch(err => {
            reject(err)
          })
          .finally(() => {
            commit('SET_ADDING_FIREWALL', false)
          })
      })
    },
    updateFirewall({ commit }, payload) {
      commit('SET_UPDATING_FIREWALL', true)
      return new Promise((resolve, reject) => {
        api('compute')
          .put(`/projects/${getProjectId()}/firewall/${payload.id}`, payload.firewall)
          .then(res => {
            commit('UPDATE_FIREWALL', res.data.result)
            resolve(res)
          })
          .catch(err => {
            reject(err)
          })
          .finally(() => {
            commit('SET_UPDATING_FIREWALL', false)
          })
      })
    },
    removeFirewall({ commit }, id) {
      commit('SET_REMOVING_FIREWALL', true)
      return new Promise((resolve, reject) => {
        api('compute')
          .delete(`/projects/${getProjectId()}/firewall/${id}`)
          .then(res => {
            commit('REMOVE_FIREWALL', id)
            resolve(res)
          })
          .catch(err => {
            reject(err)
          })
          .finally(() => {
            commit('SET_REMOVING_FIREWALL', false)
          })
      })
    },
    addRule({ commit }, payload) {
      commit('SET_ADDING_RULE', true)
      return new Promise((resolve, reject) => {
        api('compute')
          .post(`/projects/${getProjectId()}/firewall/${payload.firewallId}/rules`, payload.rule)
          .then(res => {
            commit('ADD_RULE', res.data.result)
            resolve(res)
          })
          .catch(err => {
            reject(err)
          })
          .finally(() => {
            commit('SET_ADDING_RULE', false)
          })
      })
    },
    updateRule({ commit }, payload) {
      commit('SET_UPDATING_RULE', true)
      return new Promise((resolve, reject) => {
        api('compute')
          .put(`/projects/${getProjectId()}/firewall/${payload.firewallId}/rules/${payload.id}`, payload.rule)
          .then(res => {
            commit('UPDATE_RULE', {
              id: payload.id,
              rule: res.data.result,
            })
            resolve(res)
          })
          .catch(err => {
            reject(err)
          })
          .finally(() => {
            commit('SET_UPDATING_RULE', false)
          })
      })
    },
    removeRule({ commit }, payload) {
      commit('SET_REMOVING_RULE', true)
      return new Promise((resolve, reject) => {
        api('compute')
          .delete(`/projects/${getProjectId()}/firewall/${payload.firewallId}/rules/${payload.id}`)
          .then(res => {
            commit('REMOVE_RULE', payload.id)
            resolve(res)
          })
          .catch(err => {
            reject(err)
          })
          .finally(() => {
            commit('SET_REMOVING_RULE', false)
          })
      })
    },
  },
}
